/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `AppContext` for the Caintra Connect frontend.
 * @module context/AppContext
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { createContext, useState, useCallback, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getLanguageOptions } from "../data/selectOptions";

const AppContext = createContext(null);

const AppProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const languageOptions = useMemo(
    () => getLanguageOptions(t),
    [t, i18n.language],
  );
  const [language, setLangauge] = useState(
    () =>
      languageOptions.find((option) => option.value === i18n.language) ||
      languageOptions[0],
  );
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    setLangauge(
      languageOptions.find((option) => option.value === i18n.language) ||
        languageOptions[0],
    );
  }, [languageOptions, i18n.language]);

  const changeLanguage = useCallback(
    (lang) => {
      setLangauge(lang);
      i18n.changeLanguage(lang.value);
      dayjs.locale(lang.value); // use locale globally
    },
    [i18n],
  );

  const changePermissions = useCallback((newPerms) => {
    setPermissions(newPerms);
  }, []);

  return (
    <AppContext
      value={{
        language,
        changeLanguage,
        permissions,
        changePermissions,
      }}
    >
      {children}
    </AppContext>
  );
};

export { AppContext, AppProvider };
